/** Responsive comparison grid — columns on desktop, tabs on mobile (FR-CMP-02, FR-CMP-04). */

import { useState } from "react";
import { Box, Grid2 as Grid, Paper, Typography, Tabs, Tab, useMediaQuery, useTheme } from "@mui/material";
import ResultViewer from "@/components/ResultViewer";

interface CompareItem {
  jobId: string;
  pluginId: string;
  pluginName?: string;
  createdAt: string;
  parameters: Record<string, unknown>;
  content?: string | Record<string, unknown>;
  format?: string;
  downloadOnly?: boolean;
  error?: string;
}

interface Props {
  items: CompareItem[];
}

export default function ComparisonGrid({ items }: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [tab, setTab] = useState(0);

  if (isMobile) {
    return (
      <Box>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable">
          {items.map((item, i) => (
            <Tab key={item.jobId} label={`Job ${i + 1}`} />
          ))}
        </Tabs>
        {items[tab] && <Panel item={items[tab]!} />}
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      {items.map((item) => (
        <Grid key={item.jobId} size={{ xs: 12, sm: 6, md: items.length <= 2 ? 6 : 4, lg: 12 / items.length }}>
          <Panel item={item} />
        </Grid>
      ))}
    </Grid>
  );
}

function Panel({ item }: { item: CompareItem }) {
  const company = (item.parameters?.company_search as Record<string, string>)?.name;
  return (
    <Paper variant="outlined" sx={{ p: 2, height: "100%", overflow: "auto", maxHeight: 600 }}>
      <Typography variant="subtitle2">{item.pluginName ?? item.pluginId}</Typography>
      {company && <Typography variant="body2" color="text.secondary">{company}</Typography>}
      <Typography variant="caption" color="text.secondary">{new Date(item.createdAt).toLocaleString()}</Typography>
      <Box sx={{ mt: 1 }}>
        {item.error ? (
          <Typography color="error">{item.error}</Typography>
        ) : item.content ? (
          <ResultViewer content={item.content} format={item.format ?? "markdown"} downloadOnly={item.downloadOnly} />
        ) : (
          <Typography color="text.secondary">No result</Typography>
        )}
      </Box>
    </Paper>
  );
}
